import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

/**
 * Shared, in-memory cache for the signed-in user's session data.
 * Every protected page used to re-fetch roles/profile on mount, causing a
 * full-screen spinner on each navigation. Now the first load is shared and
 * later mounts read the resolved values synchronously.
 */

const withTimeout = async <T,>(promise: PromiseLike<T>, ms = 8000): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error("Zeitüberschreitung")), ms);
  });
  try {
    return await Promise.race([promise, timeout]);
  } finally {
    clearTimeout(timer!);
  }
};

let lastUser: User | null | undefined = undefined;
export const getCachedUser = () => lastUser;
export const setCachedUser = (u: User | null) => {
  if (lastUser && (!u || u.id !== lastUser.id)) clearSessionCache();
  lastUser = u;
};

type Entry<T> = { promise: Promise<T>; value?: T; done: boolean };
const store = new Map<string, Entry<unknown>>();

const cached = <T,>(key: string, loader: () => Promise<T>): Entry<T> => {
  const existing = store.get(key) as Entry<T> | undefined;
  if (existing) return existing;
  const entry: Entry<T> = { done: false, promise: Promise.resolve() as Promise<T> };
  entry.promise = loader().then(
    (v) => { entry.value = v; entry.done = true; return v; },
    (e) => { store.delete(key); throw e; },
  );
  store.set(key, entry as Entry<unknown>);
  return entry;
};

export const peek = <T,>(key: string): T | undefined => {
  const e = store.get(key) as Entry<T> | undefined;
  return e?.done ? e.value : undefined;
};

export const loadRoles = (userId: string) =>
  cached(`roles:${userId}`, async () => {
    const { data } = await withTimeout(supabase.from("user_roles").select("role").eq("user_id", userId));
    return (data ?? []).map((r) => r.role as string);
  }).promise;
export const peekRoles = (userId: string) => peek<string[]>(`roles:${userId}`);

export const loadAffiliateRow = <T,>(userId: string) =>
  cached(`aff:${userId}`, async () => {
    const { data } = await withTimeout(supabase.from("affiliates").select("*").eq("user_id", userId).maybeSingle());
    return (data as T) ?? null;
  }).promise as Promise<T | null>;
export const peekAffiliateRow = <T,>(userId: string) => peek<T | null>(`aff:${userId}`);

export type CachedProfile = { full_name: string; email: string };
export const loadProfile = (userId: string) =>
  cached(`profile:${userId}`, async () => {
    const { data } = await withTimeout(supabase.from("profiles").select("full_name, email").eq("id", userId).maybeSingle());
    return (data as CachedProfile) ?? null;
  }).promise;
export const peekProfile = (userId: string) => peek<CachedProfile | null>(`profile:${userId}`);

export const clearSessionCache = () => store.clear();
